/**
 * Lecture typée et validante d'un document JSON — EF-RBK-005, EF-PAR-003.
 *
 * Chaque échec nomme le champ fautif, comme l'exige la section 17 : « Refuser
 * l'import sans remplacer les données courantes, en nommant le champ fautif. »
 *
 * Un champ inconnu rencontré dans un document de `schemaVersion` 1 est ignoré
 * silencieusement : le lecteur ne consulte que les champs qu'il connaît.
 */

import { DomainError } from '../core/errors';

export function schemaError(path: string, expectation: string): DomainError {
  return new DomainError('document/schema', `Champ « ${path} » : ${expectation}.`, path);
}

export class JsonReader {
  constructor(
    private readonly path: string,
    private readonly value: unknown,
  ) {}

  static root(value: unknown): JsonReader {
    return new JsonReader('$', value);
  }

  get isAbsent(): boolean {
    return this.value === undefined;
  }

  get isNull(): boolean {
    return this.value === null;
  }

  get location(): string {
    return this.path;
  }

  /** Champ obligatoire : son absence est un refus explicite. */
  required(name: string): JsonReader {
    const record = this.object();
    const child = record[name];
    if (child === undefined) throw schemaError(childPath(this.path, name), 'champ obligatoire absent');
    return new JsonReader(childPath(this.path, name), child);
  }

  /** Champ facultatif : retourne un lecteur vide plutôt qu'une erreur. */
  optional(name: string): JsonReader {
    const record = this.object();
    return new JsonReader(childPath(this.path, name), record[name]);
  }

  object(): Record<string, unknown> {
    if (typeof this.value !== 'object' || this.value === null || Array.isArray(this.value)) {
      throw schemaError(this.path, 'un objet est attendu');
    }
    return this.value as Record<string, unknown>;
  }

  array(): readonly JsonReader[] {
    if (!Array.isArray(this.value)) throw schemaError(this.path, 'un tableau est attendu');
    return this.value.map((item, index) => new JsonReader(`${this.path}[${index}]`, item));
  }

  string(): string {
    if (typeof this.value !== 'string') throw schemaError(this.path, 'une chaîne est attendue');
    return this.value;
  }

  number(): number {
    if (typeof this.value !== 'number' || !Number.isFinite(this.value)) {
      throw schemaError(this.path, 'un nombre fini est attendu');
    }
    return this.value;
  }

  integer(): number {
    const value = this.number();
    if (!Number.isInteger(value)) throw schemaError(this.path, 'un entier est attendu');
    return value;
  }

  boolean(): boolean {
    if (typeof this.value !== 'boolean') throw schemaError(this.path, 'un booléen est attendu');
    return this.value;
  }

  /** Valeur littérale imposée, par exemple `format` ou `schemaVersion`. */
  literal<T extends string | number>(expected: T): T {
    if (this.value !== expected) {
      throw schemaError(this.path, `la valeur ${JSON.stringify(expected)} est attendue`);
    }
    return expected;
  }

  oneOf<T extends string>(allowed: readonly T[]): T {
    const value = this.string();
    if (!(allowed as readonly string[]).includes(value)) {
      throw schemaError(this.path, `une valeur parmi ${allowed.join(', ')} est attendue`);
    }
    return value as T;
  }

  /** Applique un lecteur, ou retourne `null` lorsque la valeur est `null`. */
  nullable<T>(read: (reader: JsonReader) => T): T | null {
    if (this.value === null) return null;
    return read(this);
  }

  /** Valeur par défaut lorsque le champ est absent ou `null`. */
  withDefault<T>(fallback: T, read: (reader: JsonReader) => T): T {
    if (this.value === undefined || this.value === null) return fallback;
    return read(this);
  }

  numberInRange(min: number, max: number): number {
    const value = this.number();
    if (value < min || value > max) {
      throw schemaError(this.path, `une valeur comprise entre ${min} et ${max} est attendue`);
    }
    return value;
  }

  integerInRange(min: number, max: number): number {
    const value = this.integer();
    if (value < min || value > max) {
      throw schemaError(this.path, `un entier compris entre ${min} et ${max} est attendu`);
    }
    return value;
  }

  multipleOf(step: number): number {
    const value = this.integer();
    if (value % step !== 0) throw schemaError(this.path, `un multiple de ${step} est attendu`);
    return value;
  }
}

function childPath(parent: string, name: string): string {
  return parent === '$' ? name : `${parent}.${name}`;
}

/** Violation d'un invariant métier, distincte d'une non-conformité au schéma. */
export function invariantError(invariant: string): DomainError {
  return new DomainError(
    'document/invariant',
    `Le document est conforme au schéma mais viole un invariant métier : ${invariant}.`,
    invariant,
  );
}
